class CartRecommendations extends HTMLElement {
  constructor() {
    super();
    this.slider = null;
  }

  connectedCallback() {
    this.performRecommendations();
  }

  performRecommendations() {
    const recommendations = this.querySelector(
      '[data-recommendations]'
    );
    if (!recommendations) return;
    /* BHL 2026-07-10 · cart-aware union (was: single fetch keyed to cart.items[0], i.e. the
       NEWEST add — whichever piece was added last dictated the whole rail). Now: fetch
       complementary recs for up to the first 4 cart items in parallel, dedupe by product URL,
       rank by how many cart items voted for a product (ties: first-seen), cap at 6. Each
       server render already excludes in-cart products, so the rail becomes "the pieces your
       set is still missing" regardless of add order. Falls back to the single data-url fetch
       when data-cart-products is absent. */
    const ids = (this.dataset.cartProducts || '')
      .split(',')
      .filter(Boolean)
      .slice(0, 4);
    const urls = ids.length
      ? ids.map(id =>
          this.dataset.url.replace(/product_id=\d+/, 'product_id=' + id)
        )
      : [this.dataset.url];
    Promise.all(
      urls.map(u =>
        fetch(u)
          .then(r => (r.ok ? r.text() : null))
          .catch(() => null)
      )
    ).then(texts => {
      const votes = new Map();
      let order = 0;
      texts.forEach(text => {
        if (!text) return;
        new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelectorAll(
            '[data-recommendations] .cart-recommendations__item'
          )
          .forEach(item => {
            const link = item.querySelector('a[href*="/products/"]');
            const key = link
              ? link.getAttribute('href').split('?')[0]
              : 'k' + order;
            const entry = votes.get(key);
            if (entry) entry.count += 1;
            else votes.set(key, { node: item, count: 1, order: order++ });
          });
      });
      if (!votes.size) {
        /* BHL 2026-07-12 · set-completion state (McKenzie): every server render came back with
           zero suggestions → every set piece is already in the bag. Show the confirmation
           instead of silently hiding the rail. Guarded on at least one render actually
           succeeding (texts.some) so a network failure can't fake a completed set. The
           server-side variant (liquid, data-set-complete) wins when the app's automatic
           "Complete The Set" discount is already visible on the cart. */
        if (texts.some(Boolean)) this.showSetComplete();
        return;
      }
      const ranked = [...votes.values()]
        .sort((a, b) => b.count - a.count || a.order - b.order)
        .slice(0, 6);
      recommendations.innerHTML = '';
      ranked.forEach(r => recommendations.appendChild(r.node));
      this.classList.remove('hidden');
      /* liquid already rendered the confirmation subtitle when the set discount is on the
         cart · in that state the rail itself stays hidden (nothing left to pitch) */
      if (this.querySelector('[data-set-complete]')) {
        this.showSetComplete();
        return;
      }
      this.initSlider();
    });
  }

  showSetComplete() {
    const sub = this.querySelector('.cart-recommendations__subtitle');
    if (sub && !sub.hasAttribute('data-set-complete')) {
      sub.textContent = 'Set complete 🖤 Your 10% off is applied at checkout.';
      sub.classList.add('cart-recommendations__subtitle--complete');
    }
    const body = this.querySelector('.cart-recommendations__body');
    if (body) body.setAttribute('hidden', '');
    this.classList.remove('hidden');
    this.classList.add('is-complete');
  }

  initSlider(attempt = 0) {
    const el = this.querySelector('.swiper');
    if (!el) return;
    /* BHL 2026-07-10 · Swiper loads deferred and may land after this init fires
       (or, pre-fix, never landed at all on non-cro pages) — retry briefly instead
       of throwing and leaving the rail frozen. */
    if (typeof Swiper === 'undefined') {
      if (attempt < 40) setTimeout(() => this.initSlider(attempt + 1), 150);
      return;
    }
    if (el.swiper) el.swiper.destroy(true, false);
    this.slider = new Swiper(el, {
      slidesPerView: 'auto',
      spaceBetween: 20,
      grabCursor: true,                    /* desktop: hand cursor + mouse drag */
      mousewheel: { forceToAxis: true }    /* trackpad horizontal swipe scrolls the rail */
    });
  }
}

customElements.define('cart-recommendations', CartRecommendations);

/* BHL 2026-07-10 · compact card controls (drawer + /cart).
   2026-07-12 (McKenzie final cart copy): the SELECT SIZE toggle + chip panel is replaced by a
   .js-mini-size-select dropdown with an always-visible ADD TO BAG (.js-mini-add) beneath it.
   The add path reuses Sahara's cart-drawer section-render recipe (same as sticky-atc.js and
   product-form.js): POST /cart/add with sections + renderContents → drawer + counter re-render.
   Delegated on document so it survives every cart section re-render. The old toggle handler is
   kept for any surface still rendering the legacy trigger. */
document.addEventListener('change', e => {
  const sel = e.target.closest && e.target.closest('.js-mini-size-select');
  if (!sel) return;
  const card = sel.closest('card-product');
  if (card) card.dataset.selectedVariantId = sel.value || '';
});

document.addEventListener('click', e => {
  const add = e.target.closest('.js-mini-add');
  if (add) {
    const card = add.closest('card-product');
    const sel = card && card.querySelector('.js-mini-size-select');
    const vid = sel && sel.value;
    if (!vid) {
      if (sel) {
        sel.focus();
        card.classList.add('mini-needs-size');
        setTimeout(() => card.classList.remove('mini-needs-size'), 1400);
      }
      return;
    }
    if (add.classList.contains('is-busy')) return;
    add.classList.add('is-busy');
    const cartDrawer = document.querySelector('cart-drawer');
    const fd = new FormData();
    fd.append('id', vid);
    fd.append('quantity', '1');
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      fd.append('sections', cartDrawer.getSectionsToRender().map(s => s.section).join(','));
      fd.append('sections_url', window.location.pathname);
    }
    fetch('/cart/add', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: fd
    })
      .then(r => r.json())
      .then(resp => {
        if (resp.status) {
          console.error('mini-card add error:', resp.description || resp.message);
          add.classList.remove('is-busy');
          return;
        }
        if (cartDrawer && typeof cartDrawer.renderContents === 'function' && resp.sections) {
          cartDrawer.renderContents(resp);
        } else {
          window.location.reload();
        }
      })
      .catch(err => {
        console.error('mini-card add network error:', err);
        add.classList.remove('is-busy');
      });
    return;
  }

  const toggle = e.target.closest('.js-mini-size-toggle');
  if (toggle) {
    const card = toggle.closest('card-product');
    const panel = card && card.querySelector('.card-cart-mini__sizes');
    if (!panel) return;

    const willOpen = panel.hasAttribute('hidden');

    // accordion: one open panel per recommendation list
    const list = toggle.closest('[data-recommendations]');
    if (list) {
      list.querySelectorAll('.card-cart-mini__sizes:not([hidden])').forEach(openPanel => {
        openPanel.setAttribute('hidden', '');
        const openToggle = openPanel
          .closest('card-product')
          ?.querySelector('.js-mini-size-toggle');
        if (openToggle) openToggle.setAttribute('aria-expanded', 'false');
      });
    }

    if (willOpen) {
      panel.removeAttribute('hidden');
      toggle.setAttribute('aria-expanded', 'true');
    }
    return;
  }

  const directAdd = e.target.closest('.js-mini-direct-add');
  if (directAdd) {
    const card = directAdd.closest('card-product');
    const chip = card && card.querySelector('.card-cart-mini__sizes .js-btn-size:not(.is-disabled)');
    if (chip) chip.click();
  }
});
